import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';

import FormFlow from 'components/formflow/FormFlow';
import { connectBrokerageFlow } from 'modules/brokerageFlow/flows';
import {
  COMMUNICATION_AIDS_FIELD,
  CONNECT_FIELD,
  CONTACT_METHOD_FIELD,
  EMAIL_FIELD,
  FIRST_NAME_FIELD,
  INTERPRETER_REQUIRED_FIELD,
  LANGUAGE_FIELD,
  LAST_NAME_FIELD,
  LOCATION_FIELD,
  NDIS_PLAN_FIELD,
  PAGE_COMMUNICATION,
  PAGE_CONTACT_INFO,
  PAGE_NDIS_INFO,
  PAGE_PARTICIPANT_DETAILS,
  PARTICIPANT_AGE_RANGE_FIELD,
  PARTICIPANT_FIRST_NAME_FIELD,
  PARTICIPANT_GENDER_FIELD,
  PARTICIPANT_LAST_NAME_FIELD,
  PHONE_FIELD,
  RELATIONSHIP_FIELD,
  SERVICE_FOR_TYPE_MY_CLIENT,
  SERVICE_FOR_TYPE_MYSELF,
  SERVICES_FOR_FIELD,
  SERVICES_NEEDED_FIELD,
  TERMS_AND_CONDITION_FIELD,
  PLAN_START_DATE_FIELD,
  NDIS_PLAN_PERIOD_FIELD, TELEHEALTH_FIELD, ONLINE_SERVICE_FIELD,
} from 'modules/brokerageFlow/constants';
import { useGetApi, AuthAPI, ProviderAPI, TaxonomyAPI } from 'providerSite/api';
import Spinner from 'components/Spinner';
import {
  FIELD_AGE_GROUP,
  FIELD_COMMUNICATION_AIDS,
  FIELD_CONTACT_PARTICIPANT,
  FIELD_FIRST_NAME,
  FIELD_GENDER,
  FIELD_INTERPRETER_REQUIRED,
  FIELD_LANGUAGES,
  FIELD_LAST_NAME,
  FIELD_LOCATION,
  FIELD_PLAN_TYPE,
  FIELD_PLAN_START_DATE,
  FIELD_PLAN_DURATION, FIELD_TELEHEALTH, FIELD_ONLINE_SERVICE,
} from '../../../components/ParticipantDetailsForm/constants';


const ServiceRequestFlow = ({ participantData, serviceData }) => {

  const { uuid: providerUuid } = useParams();
  const [completedPages, setCompletedPages] = useState(new Set([
    PAGE_CONTACT_INFO,
    PAGE_PARTICIPANT_DETAILS,
    PAGE_COMMUNICATION,
    PAGE_NDIS_INFO,
  ]));

  const { data: taxonomyAgeGroups } = useGetApi(TaxonomyAPI.GET_TAXONOMY, {
    queryParams: {name: 'age_group'},
    staleTime: 60,
  });

  const { data: _profile } = useGetApi(AuthAPI.GET_PROFILE, {
    staleTime: 60,
  });

  const profile = _profile?.profile;

  const { data: provider } = useGetApi(ProviderAPI.GET_PROVIDER, {
    params: {uuid: providerUuid},
    staleTime: 60,
  });


  if (!profile || !provider || !taxonomyAgeGroups)
    return <Spinner />;

  const _service = {
    id: serviceData.serviceId,
    name: serviceData.serviceName
  };

  const participantFirstName = participantData[FIELD_FIRST_NAME];
  const participantLastName = participantData[FIELD_LAST_NAME];

  const shouldContactParticipant =
    participantData[FIELD_CONTACT_PARTICIPANT].toLowerCase() === 'participant';

  const planStartDate = participantData?.[FIELD_PLAN_START_DATE];

  const preFilledData = {
    [NDIS_PLAN_FIELD]: participantData[FIELD_PLAN_TYPE],
    [SERVICES_FOR_FIELD]: shouldContactParticipant ?
      SERVICE_FOR_TYPE_MYSELF : SERVICE_FOR_TYPE_MY_CLIENT,
    [LOCATION_FIELD]: participantData[FIELD_LOCATION],
    [SERVICES_NEEDED_FIELD]: [
      _service,
    ],
    _qualifiedServices: {
      matchedServices: [
        {
          ..._service,
          categoryName: serviceData.serviceCategoryName,
        },
      ],
      unMatchedServices: [],
    },
    _ageGroups: taxonomyAgeGroups.map(item => item.name),
    [CONNECT_FIELD]: {
      connectMode: true,
      participantClientUuid: participantData.uuid,
    },
    [RELATIONSHIP_FIELD]: !shouldContactParticipant && 'Support Coordinator',
    [FIRST_NAME_FIELD]: shouldContactParticipant ? participantFirstName : profile.firstName,
    [LAST_NAME_FIELD]: shouldContactParticipant ? participantLastName : profile.lastName,
    [PHONE_FIELD]: shouldContactParticipant ? participantData.phone : profile.contactPhone,
    [EMAIL_FIELD]: shouldContactParticipant ? participantData.email : profile.email,
    [CONTACT_METHOD_FIELD]: 'Any',
    [PARTICIPANT_FIRST_NAME_FIELD]: participantFirstName,
    [PARTICIPANT_LAST_NAME_FIELD]: participantLastName,
    [PARTICIPANT_AGE_RANGE_FIELD]: participantData[FIELD_AGE_GROUP],
    [PARTICIPANT_GENDER_FIELD]: participantData[FIELD_GENDER],
    [LANGUAGE_FIELD]: participantData?.[FIELD_LANGUAGES].join(', '),
    [INTERPRETER_REQUIRED_FIELD]: participantData[FIELD_INTERPRETER_REQUIRED],
    [COMMUNICATION_AIDS_FIELD]: participantData?.[FIELD_COMMUNICATION_AIDS],
    [PLAN_START_DATE_FIELD]: planStartDate && dayjs(planStartDate).format('DD/MM/YYYY'),
    [NDIS_PLAN_PERIOD_FIELD]: participantData?.[FIELD_PLAN_DURATION],
    [TERMS_AND_CONDITION_FIELD]: true,
    [TELEHEALTH_FIELD]: participantData?.[FIELD_TELEHEALTH],
    [ONLINE_SERVICE_FIELD]: participantData?.data?.[FIELD_ONLINE_SERVICE],

  };

  return (
    <FormFlow
      flow={connectBrokerageFlow} onComplete={() => {}}
      completedPages={completedPages}
      setCompletedPages={setCompletedPages}
      defaultsToLastValidPage={true}
      forcedDefaults={preFilledData}
      connectMode
      providerUuid={providerUuid}
    />
  );
};

ServiceRequestFlow.propTypes = {
  participantData: PropTypes.object,
  serviceData: PropTypes.object,
};

export default ServiceRequestFlow;